import { Tooltip } from "@material-tailwind/react";
import { useRef } from "react";
import { PrimaryButton } from "../buttons/PrimaryButton";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";

/**
 * Example usage:
 * <ConcentricStats
 *   stats={[
 *     { label: "Downloads", value: 100, color: "bg-blue-500" },
 *     { label: "Droppers", value: 80, color: "bg-green-500" },
 *     { label: "Regular Droppers", value: 50, color: "bg-yellow-500" },
 *   ]}
 * />
 */

export const ConcentricCircles = ({
  stats,
}: {
  stats: { label: string; value: number; style: string; tooltip: string }[];
}) => {
  /** Refs */
  const snapshotRef = useRef(null);
  /** Hooks */
  const [, takeScreenshot] = useScreenshot();

  /** Callbacks */
  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      // Create a link to download the image
      const link = document.createElement("a");
      link.href = image;
      link.download = "user_breakdown.png"; // Set the default file name
      link.click(); // Programmatically click the link to trigger download
    });
  };

  if (!stats || stats.length === 0) return null;

  // Sort stats by value descending
  const sortedStats = [...stats].sort((a, b) => b.value - a.value);
  const maxValue = sortedStats[0].value;

  // Calculate relative sizes (min 40%)
  const circles = sortedStats.map((stat, i) => {
    const sizePercent = stat.value / maxValue;
    // Ensure minimum size for visibility
    const size = Math.max(1, sizePercent * 100);
    return { ...stat, size, z: stats.length + i };
  });

  return (
    <div className="flex flex-col w-full max-w-full overflow-hidden rounded-xl p-4 sm:p-6 bg-white border-gray-border shadow-none">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between mb-1">
        <p></p>
        <PrimaryButton
          size="sm"
          onClick={downloadScreenshot}
          label="Download image"
          className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
        />
      </div>
      <div
        ref={snapshotRef}
        className="flex items-center justify-center flex-col w-full"
      >
        <p className="font-satoshi text-start w-full  font-bold text-lg sm:text-2xl text-tailgrids-black  sm:mb-0">
          User Stats Overview
        </p>
        <div className="relative mt-3 flex items-center justify-center w-full max-w-[400px] min-w-[260px] aspect-square">
          {circles.map((circle) => {
            // Position all circles at the center, aligned to bottom
            const yOffset = 100 - circle.size;

            return (
              <Tooltip key={circle.label} content={circle.tooltip}>
                <div
                  className={`absolute flex flex-col items-center justify-start rounded-full transition-all duration-300 ${circle.style}`}
                  style={{
                    width: `${circle.size}%`,
                    height: `${circle.size}%`,
                    zIndex: circle.z,
                    left: `${50 - circle.size / 2}%`,
                    top: `${yOffset}%`,
                  }}
                >
                  <span className="font-satoshi text-dark font-bold text-xl mt-2">
                    {circle.value}
                  </span>
                  <span className="font-satoshi text-dark text-sm mt-0.5 text-center px-2 whitespace-nowrap">
                    {circle.label}
                  </span>
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
};
